import {
    AimsStatus,
    CanclePolicyResponse,
    MpesaPaymentRequest,
    MTNPaymentRequest,
    PolicyFlag,
    PolicyState,
    PushToAmisResponse,
    ValidateMpesaOfflinePaymentRequest,
    ValidateMpesaOfflinePaymentResponse
} from 'Application/models';
import { CanclePolicyRequest } from 'Application/models/dto/request/PolicyRequest';
import { PolicyService } from 'Application/service';
import { create } from 'zustand';

export const usePolicyStore = create<PolicyState>((set) => ({
    isLoading: false,
    isOfflinePaymentDetailsLoading: false,
    isDownloadFileLoading: false,
    isError: false,
    error: null,
    policyDocument: null,
    policyDetails: null,
    getPolicies: [],
    isPolicyDocumentValid: null,
    adminGetAllPoliciesData: [],
    downloadAllPoliciesData: [],
    MpesaPayment: null,
    policyByQuotatioNoDetails: null,
    singlepolicyByQuotatioNoDetails: null,
    dependantsDetails: null,
    MpesaOfflinePaymentDetails: null!,
    MTNPaymentStatusData: null!,
    canclePolicyData: null!,

    genaretePolicyOnCredit: async (quoteNumber: string) => {
        set({ isLoading: true });

        try {
            const response = await PolicyService.generatePolicyOnCredit(quoteNumber);
            set({
                isError: false,
                error: null,

                policyDetails: response
            });
        } catch (error) {
            set({ error: 'Failed to generate policy on credit', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    genaretePolicyOnMpesa: async (MpesaPayment: MpesaPaymentRequest) => {
        set({ isLoading: true });

        try {
            const response = await PolicyService.generatePolicyOnMpesa(MpesaPayment);
            set({
                isError: false,
                error: null,

                policyDetails: response
            });
        } catch (error) {
            set({ error: 'Payment Failed', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },
    genaretePolicyOnMTN: async (MtnPayment: MTNPaymentRequest) => {
        set({ isLoading: true });

        try {
            const response = await PolicyService.generatePolicyOnMTN(MtnPayment);
            set({
                isError: false,
                error: null,
                policyDetails: response
            });

            return response; // ✅ Return response to caller
        } catch (error) {
            set({ error: "Payment Failed", isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    genaretePolicyOnMpesaOffline: async (validatePaymentDetails: ValidateMpesaOfflinePaymentRequest, quotationNumber: string) => {
        set({ isLoading: true });

        try {
            const response = await PolicyService.generatePolicyOnMpesaOffline(quotationNumber, validatePaymentDetails);
            set({
                isError: false,
                error: null,
                policyDetails: response
            });
        } catch (error) {
            set({ error: 'Payment Failed', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    getPolicyDocument: async (policyNumber: string) => {
        set({ isDownloadFileLoading: true });

        try {
            const response = await PolicyService.getPolicyDocument(policyNumber);
            set({
                isError: false,
                error: null
            });

            return response;
        } catch (error) {
            set({ error: 'Failed to get policy document', isError: true });
            throw error;
        } finally {
            set({ isDownloadFileLoading: false });
        }
    },
    getPolicyUploadedFiles: async (fileName: string) => {
        set({ isDownloadFileLoading: true });

        try {
            const response = await PolicyService.getPolicyUpladedFiles(fileName);
            set({
                isError: false,
                error: null
            });

            return response;
        } catch (error) {
            set({ error: 'Failed to get policy document', isError: true });
            throw error;
        } finally {
            set({ isDownloadFileLoading: false });
        }
    },
    getAllPolicy: async (agencyName: string, page: number, pageSize: number, startDate?: string, endDate?: string) => {
        set({ isLoading: true });

        try {
            const response = await PolicyService.fetchPolicy(agencyName, page, pageSize, startDate, endDate);
            set({
                isError: false,
                error: null
            });

            return response;
        } catch (error) {
            set({ error: 'Failed to get policy', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },
    downloadAllPolicy: async (
        pageNumber: number,
        pageSize: number,
        flag: PolicyFlag,
        transactionStatus?: AimsStatus,
        searchValue?: string,
        startDate?: string,
        endDate?: string,
        agencyName?: string,
        regionName?: string
    ) => {
        set({ isLoading: true });

        try {
            const response = await PolicyService.downloadAllPolicies(
                pageNumber,
                pageSize,
                flag,
                transactionStatus,
                searchValue,
                startDate,
                endDate,
                agencyName,
                regionName
            );
            set({
                isError: false,
                error: null,
                downloadAllPoliciesData: response
            });
        } catch (error) {
            set({ error: 'Failed to get policy', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    getMpesaOfflinePaymentDetails: async (quotationNumber: string) => {
        set({ isOfflinePaymentDetailsLoading: true });

        try {
            const response = await PolicyService.fetchMpesaOfflinePaymentDetails(quotationNumber);
            set({
                isError: false,
                error: null,
                MpesaOfflinePaymentDetails: response,
                policyDetails: response
            });
        } catch (error) {
            set({ error: 'Failed to get policy', isError: true });
            throw error;
        } finally {
            set({ isOfflinePaymentDetailsLoading: false });
        }
    },
    getMTNPaymentStatus: async (referenceId: string) => {
        set({ isOfflinePaymentDetailsLoading: true });

        try {
            const response = await PolicyService.fetchMTNPaymentStatus(referenceId);
            set({
                isError: false,
                error: null,
                MTNPaymentStatusData: response,
                policyDetails: response
            });

            return response;
        } catch (error) {
            set({ error: 'Failed to get policy', isError: true });
            throw error;
        } finally {
            set({ isOfflinePaymentDetailsLoading: false });
        }
    },
    getPolicyByQuotationNo: async (quotationNumber: string) => {
        set({ isLoading: true });

        try {
            const response = await PolicyService.fetchPolicyQuotation(quotationNumber);
            set({
                isError: false,
                error: null,
                policyByQuotatioNoDetails: response,
                policyDetails: response
            });
        } catch (error) {
            set({ error: 'Failed to get policy', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },
    getSinglePolicyByQuotationNo: async (policyNumber: string) => {
        set({ isLoading: true });

        try {
            const response = await PolicyService.fetchPolicyByPolicyNo(policyNumber);
            set({
                isError: false,
                error: null,
                singlepolicyByQuotatioNoDetails: response,
                policyDetails: response
            });
        } catch (error) {
            set({ error: 'Failed to get policy', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },
    getDependantsDetails: async (policyNumber: string) => {
        set({ isLoading: true });

        try {
            const response = await PolicyService.fetchDepedantsDetails(policyNumber);
            set({
                isError: false,
                error: null,
                dependantsDetails: response,
                policyDetails: response
            });
        } catch (error) {
            set({ error: 'Failed to get policy', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    checkPolicyValidity: async (policyNumber: string) => {
        set({ isLoading: true });

        try {
            const response = await PolicyService.checkPolicyValidity(policyNumber);
            set({
                isPolicyDocumentValid: response,
                isError: false,
                error: null
            });

            return response;
        } catch (error) {
            set({ error: 'Failed to check policy validity', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },
    adminGetAllPolicy: async (
        page: number,
        pageSize: number,
        flag: PolicyFlag,
        status?: AimsStatus,
        searchValue?: string,
        startDate?: string,
        endDate?: string,
        agencyName?: string,
        regionName?: string
    ) => {
        set({ isLoading: true });

        try {
            const response = await PolicyService.getAllPolicies(
                page,
                pageSize,
                flag,
                status,
                searchValue,
                startDate,
                endDate,
                agencyName,
                regionName
            );
            set({
                adminGetAllPoliciesData: response, // Update with fetched data
                isError: false,
                error: null
            });

            return response;
        } catch (error) {
            set({ error: 'Failed to get policy', isError: true });
            throw error;
        } finally {
            set({ isLoading: false });
        }
    },

    pushToAMis: async (
        policyNumber: string,
        page: number,
        pageSize: number,
        flag: PolicyFlag,
        status?: AimsStatus
    ): Promise<PushToAmisResponse> => {
        set({ isLoading: true });

        try {
            const response = await PolicyService.pushToAims(policyNumber);

            // Reset error state on success
            set({ isError: false, error: null });

            // Sync policies
            await usePolicyStore.getState().adminGetAllPolicy(page, pageSize, flag, status);

            return { error: false, message: 'Policy pushed to AIMS successfully' };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            set({ error: errorMessage, isError: true });
            return { error: true, message: errorMessage };
        } finally {
            set({ isLoading: false });
        }
    },

    canclePolicy: async (canclePolicyData: CanclePolicyRequest, page, pageSize, flag) => {
        set({ isLoading: true });

        try {
            const response = await PolicyService.canclePolicy(canclePolicyData);

            // Reset error state on success
            set({ isError: false, error: null });

            // Refresh policies
            await usePolicyStore.getState().adminGetAllPolicy(page, pageSize, flag);
            console.log(response);

            return { error: false, message: 'Policy canceled successfully' };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            set({ error: errorMessage, isError: true });
            return { error: true, message: errorMessage };
        } finally {
            set({ isLoading: false });
        }
    }
}));
